import React, { useEffect, useState, useMemo } from "react";
import "../../Styles/invoiceTable.css";
import { toast } from "react-toastify";
import { BsSearch } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Requisition = {
  itemName: string;
  approvalNumber: string;
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  price: any;
  quantity: string;
  _id: string;
  loadingSlip: any;
  customer: { businessName: string };
  firstName: string;
  staff: { name: string };
  business: string;
  items: { name: string; quantity: number }[];
  businessType: string;
  requisitionRef: string;
  status: string;
  createdAt: string;
  mvtType: string;
};

const ApproveReport = () => {
  const [getRequisition, setGetRequisition] = useState<Requisition[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = window.localStorage.getItem("token");

  useEffect(() => {
    queryRequisition();
  }, [fromDate, toDate]);

  const queryRequisition = () => {
    setIsLoading(true);
    setError(null);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const queryParams =
      fromDate || toDate
        ? `?startDate=${fromDate || ""}&endDate=${toDate || ""}`
        : "";

    api
      .get(`/requisition${queryParams}`, {
        headers,
      })
      .then((res) => {
        setGetRequisition(res.data.data.requisitions);
      })
      .catch((error) => {
        if (
          error.response?.data?.message === "jwt expired" ||
          error.response?.data?.message === "jwt malformed"
        ) {
          setReLogin(true);
        } else {
          setError("Failed to fetch requisitions. Please try again later.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filteredRequisitions = useMemo(() => {
    return getRequisition.filter((req) => {
      const reqDate = new Date(req.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (startDate && reqDate < startDate) return false;
      if (endDate && reqDate > endDate) return false;

      if (searchField === "combined") {
        if (!businessQuery && !productQuery) return true;
      } else if (!searchQuery) {
        return true;
      }
      const businessName = req.customer?.businessName?.toLowerCase() || "";
      const businessNameMatch = businessName.includes(
        searchField === "combined"
          ? businessQuery.toLowerCase()
          : searchQuery.toLowerCase()
      );

      const productNameMatch = req.items?.some((item) =>
        item.name?.toLowerCase().includes(
          searchField === "combined"
            ? productQuery.toLowerCase()
            : searchQuery.toLowerCase()
        )
      );

      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "productName":
          return productNameMatch;
        case "combined":
          return businessNameMatch && productNameMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [
    getRequisition,
    fromDate,
    toDate,
    searchQuery,
    productQuery,
    businessQuery,
    searchField,
  ]);

  const paginatedRequisitions = filteredRequisitions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const calculateTotalQuantityLoaded = (requisitions: Requisition[]) => {
    return requisitions.reduce((total, requisition) => {
      return (
        total +
        requisition.items.reduce(
          (itemTotal, item) => itemTotal + (item.quantity || 0),
          0
        )
      );
    }, 0);
  };

  const calculateCustomerQuantityLoaded = (requisitions: Requisition[]) => {
    return requisitions.reduce((acc, requisition) => {
      const customerName = requisition.customer?.businessName || "Unknown";
      requisition.items.forEach((item) => {
        acc[customerName] = (acc[customerName] || 0) + (item.quantity || 0);
      });
      return acc;
    }, {} as Record<string, number>);
  };

  const totalQuantityLoaded = calculateTotalQuantityLoaded(filteredRequisitions);
  const customerQuantities = calculateCustomerQuantityLoaded(filteredRequisitions);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");
    const dowBtnElement = document.querySelector(".dowBtn");

    if (slipElement) {
      if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "none";

      const pdfOptions = {
        margin: 2,
        filename: "Approval-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf()
        .from(slipElement)
        .set(pdfOptions)
        .save()
        .finally(() => {
          window.print()
          if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "block";
        });
    }
  };

  const exportToExcel = () => {
    if (!filteredRequisitions || filteredRequisitions.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredRequisitions.flatMap((requisition, index) => {
      return requisition.items.map((item, itemIdx) => ({
        "S/N": index + 1,
        "Business Name": requisition.customer?.businessName || "N/A",
        "Product Name": item.name || "N/A",
        "Quantity": item.quantity || 0,
        "Approval Number": requisition.approvalNumber || "N/A",
        "Status": requisition.status || "N/A",
        "MVT Type": requisition.mvtType || "N/A",
        "Created By": requisition.staff?.name || "N/A",
        "Date": new Date(requisition.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ApprovalReports");
    XLSX.writeFile(wb, "Approval-Report.xlsx");
  };

  return (
    <div className="board">
      {reLogin ? <ReLoginModal /> : null}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <label htmlFor="businessQuery" className="sr-only">
              Customer Name
            </label>
            <input
              id="businessQuery"
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <label htmlFor="productQuery" className="sr-only">
              Product Name
            </label>
            <input
              id="productQuery"
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <label htmlFor="searchQuery" className="sr-only">
              Search
            </label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <label htmlFor="fromDate" className="sr-only">
            From Date
          </label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <label htmlFor="toDate" className="sr-only">
            To Date
          </label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : error ? (
          <div className="m-auto text-center px-4 py-4">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        ) : paginatedRequisitions.length > 0 ? (
          <div>
            <div className="table-wrapper">
              <table className="inTable">
                <thead>
                  <tr className="heTable">
                    <th>S/N</th>
                    <th>Customer Name</th>
                    <th>Product Name</th>
                    <th>Quantity</th>
                    <th>Approval No.</th>
                    <th>Status</th>
                    <th>MVT Type</th>
                    <th>Created By</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequisitions.map((requisition, index) =>
                    requisition.items.map((item, itemIdx) => (
                      <tr
                        key={`${requisition._id}-${itemIdx}`}
                        className="taData"
                      >
                        {itemIdx === 0 && (
                          <>
                            <td rowSpan={requisition.items.length}>
                              {page * rowsPerPage + index + 1}
                            </td>
                            <td rowSpan={requisition.items.length}>
                              {requisition.customer?.businessName || "N/A"}
                            </td>
                          </>
                        )}
                        <td>{item.name || "N/A"}</td>
                        <td>{item.quantity || 0}</td>
                        {itemIdx === 0 && (
                          <>
                            <td rowSpan={requisition.items.length}>
                              {requisition.approvalNumber || "N/A"}
                            </td>
                            <td rowSpan={requisition.items.length}>
                              {requisition.status || "N/A"}
                            </td>
                            <td rowSpan={requisition.items.length}>
                              {requisition.mvtType || "N/A"}
                            </td>
                            <td rowSpan={requisition.items.length}>
                              {requisition.staff?.name || "N/A"}
                            </td>
                            <td rowSpan={requisition.items.length}>
                              {new Date(requisition.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="aggregate">
              {Object.entries(customerQuantities).map(([customer, quantity]) => (
                <div key={customer}>
                  <strong>{customer}: </strong>
                  {quantity}
                </div>
              ))}
              <div>
                <strong>Total Quantity: </strong>
                {totalQuantityLoaded}
              </div>
            </div>
          </div>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredRequisitions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default ApproveReport;